import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

import { computed } from "vue";
import { Skeleton } from "@/components/ui/skeleton";
import StatsCard from "@/components/dashboard/users/StatsCard.vue";
import type { DashboardSummary } from "@/types/dashboard";
import { getDashboardMetricLabel } from "@/utils/dashboardStatusLabels";
import { DASHBOARD_ICONS } from "@/config/dashboardIcons";


export default /*@__PURE__*/_defineComponent({
  __name: 'DashboardMetricCards',
  props: {
    summary: { default: null },
    loading: { type: Boolean, default: false },
    role: { default: "client" },
    periodLabel: { default: "This Month" },
    hasLeadTracking: { type: Boolean, default: false }
  },
  emits: ["card-click"],
  setup(__props: any) {

const props = __props;



const cards = computed(() => {
  const summary = props.summary;
  const isClient = props.role === "client";

  if (isClient) {
    const periodText = props.periodLabel || "This Month";
    const pendingReviews = summary?.pending_client_action ?? 0;
    const inProgressCount = summary?.in_progress ?? 0;
    const completedCount = summary?.completed ?? 0;
    const openRequestsCount = summary?.ticket_requests ?? 0;

    const clientCards = [
      {
        label: "Needs Your Review",
        displayLabel: "Needs Your Review",
        value: pendingReviews,
        description:
          pendingReviews > 0
            ? "Awaiting your review or approval"
            : "All clear! Nothing pending",
        icon: DASHBOARD_ICONS.needsReview,
        iconWrapClass:
          pendingReviews > 0
            ? "bg-amber-50 border-amber-200 text-amber-600"
            : "bg-emerald-50 border-emerald-200 text-emerald-600",
        iconClass: pendingReviews > 0 ? "text-amber-600" : "text-emerald-600",
        trendText: pendingReviews > 0 ? "Action required" : "All clear",
        trendClass:
          pendingReviews > 0
            ? "text-amber-600 font-bold"
            : "text-emerald-600 font-bold",
      },
      {
        label: "In Progress",
        displayLabel: "In Progress",
        value: inProgressCount,
        description: "Tasks currently being worked on",
        icon: DASHBOARD_ICONS.inProgress,
        iconWrapClass: "bg-blue-50 border-blue-200 text-blue-600",
        iconClass: "text-blue-600",
        trendText:
          inProgressCount > 0
            ? `${inProgressCount} active`
            : "None in progress",
        trendClass: "text-blue-600 font-bold",
      },
      {
        label: "Completed",
        displayLabel: `Completed (${periodText})`,
        value: completedCount,
        description: `Deliverables finished in ${periodText.toLowerCase()}`,
        icon: DASHBOARD_ICONS.completed,
        iconWrapClass: "bg-emerald-50 border-emerald-200 text-emerald-600",
        iconClass: "text-emerald-600",
        trendText: `${completedCount} completed`,
        trendClass: "text-emerald-600 font-bold",
      },
      {
        label: "Open Requests",
        displayLabel: "Open Requests",
        value: openRequestsCount,
        description: "Active support and service requests",
        icon: DASHBOARD_ICONS.openRequests,
        iconWrapClass: "bg-purple-50 border-purple-200 text-purple-600",
        iconClass: "text-purple-600",
        trendText:
          openRequestsCount > 0
            ? `${openRequestsCount} pending`
            : "No open tickets",
        trendClass: "text-purple-600 font-bold",
      },
    ];

    if (props.hasLeadTracking) {
      clientCards.push({
        label: "Leads Captured",
        displayLabel: `Leads Captured (${periodText})`,
        value: summary?.new_leads ?? 0,
        description: `Leads captured ${periodText.toLowerCase()}`,
        icon: DASHBOARD_ICONS.leadsCaptured,
        iconWrapClass: "bg-pink-50 border-pink-200 text-pink-600",
        iconClass: "text-pink-600",
        trendText: "Connected tracking",
        trendClass: "text-pink-600 font-bold",
      });
    }

    return clientCards;
  }

  // Admin / internal view
  return [
    {
      label: "Total Tasks",
      displayLabel: getDashboardMetricLabel("Total Tasks", props.role),
      value: summary?.total_tasks ?? 0,
      description: "Across the current scope",
      icon: DASHBOARD_ICONS.totalTasks,
      iconWrapClass: "bg-blue-50 border-blue-100 text-blue-600",
      iconClass: "text-blue-600",
    },
    {
      label: "In Progress",
      displayLabel: getDashboardMetricLabel("In Progress", props.role),
      value: summary?.in_progress ?? 0,
      description: "Tasks currently moving forward",
      icon: DASHBOARD_ICONS.inProgress,
      iconWrapClass: "bg-purple-50 border-purple-100 text-purple-600",
      iconClass: "text-purple-600",
    },
    {
      label: "Completed",
      displayLabel: getDashboardMetricLabel("Completed", props.role),
      value: summary?.completed ?? 0,
      description: "Finished work in scope",
      icon: DASHBOARD_ICONS.completed,
      iconWrapClass: "bg-emerald-50 border-emerald-100 text-emerald-600",
      iconClass: "text-emerald-600",
    },
    {
      label: "Ticket Requests",
      displayLabel: "Ticket Requests",
      value: summary?.ticket_requests ?? 0,
      description: "Submitted service requests",
      icon: DASHBOARD_ICONS.openRequests,
      iconWrapClass: "bg-cyan-50 border-cyan-100 text-cyan-600",
      iconClass: "text-cyan-600",
    },
    {
      label: "Total Leads",
      displayLabel: "Total Leads",
      value: summary?.new_leads ?? 0,
      description: "Leads captured across scope",
      icon: DASHBOARD_ICONS.leadsCaptured,
      iconWrapClass: "bg-pink-50 border-pink-100 text-pink-600",
      iconClass: "text-pink-600",
    },
  ];
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", {
    class: _normalizeClass(["grid grid-cols-1 gap-4 sm:grid-cols-2", cards.value.length === 4 ? 'xl:grid-cols-4' : 'xl:grid-cols-5'])
  }, [
    (__props.loading)
      ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(__props.role === 'client' && !__props.hasLeadTracking ? 4 : 5, (n) => {
          return (_openBlock(), _createBlock(_unref(Skeleton), {
            key: n,
            class: "h-[11rem] rounded-3xl border border-slate-100 bg-white"
          }))
        }), 128))
      : (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(cards.value, (card) => {
          return (_openBlock(), _createBlock(StatsCard, {
            key: card.label,
            title: card.displayLabel,
            stats: card.value,
            description: card.description,
            icon: card.icon,
            "icon-wrap-class": card.iconWrapClass,
            "icon-class": card.iconClass,
            "trend-text": card.trendText,
            "trend-class": card.trendClass,
            tooltip: card.tooltip,
            class: "cursor-pointer hover:shadow-md transition-all duration-200 rounded-[1.5rem]",
            onClick: ($event: any) => (_ctx.$emit('card-click', card.label))
          }, null, 8, ["title", "stats", "description", "icon", "icon-wrap-class", "icon-class", "trend-text", "trend-class", "tooltip", "onClick"]))
        }), 128))
  ], 2))
}
}

})